import { useDeviceConnection } from "functions/Context";
import { KeysOf, tuple }       from "functions/FunnyTypes";
import { useAsyncEffect, useMounted } from "functions/Util";
import { DeviceConnection }                      from "functions/networking/DeviceConnection";
import { IsFromDevice, RType, TYPE_PLA, energy } from "functions/networking/NetTypes";
import { useLoader }                             from "functions/networking/useLoader";
import { useEffect, useReducer, useRef, useState } from "react";
import config from "config.json"


const MAX_SECTOR_SIZE = 55;
const SECTOR_SIZE = 50; console.assert(SECTOR_SIZE <= MAX_SECTOR_SIZE);
const STEP_WITHIN_DAY = 48; console.assert(STEP_WITHIN_DAY <= MAX_SECTOR_SIZE);

/**
 * Rozlišení jsou zaručeně seřazená tak, aby nejjemnější mělo nejnižší číslo
 */
export enum Resolution {
  qh = 0,
  hour = 1,
  day = 2,
  month = 3
}

/**
 * Předpokládá se, že rozdíl po sobě jdoucích hodnot se vejde do "normálního" čísla, tj. je menší než něco jako 2^50.
 * Z nejakeho duvodu se hodnoty deli 10.
 */
function vsub_div_10<n extends number>(a: tuple<bigint, n>, b: tuple<bigint, n>) {
  return a.map((v, i) => Math.abs(Number(BigInt.asIntN(64, v - b[i])) / 10)) as tuple<number, n>;
}
/**
 * vektorove +=
 */
function vinc<n extends number>(a: tuple<number, n>, b: tuple<number, n>) {
  for (let i = 0; i < a.length; i++) {
    a[i] += b[i];
  }
  return a;
}

export type EnergyData = {
  time: Date,
  values: tuple<number, 6>
}[];


type FSEntry<Data extends object>
= { Status: "Error" }
| ({ Status: "Success" } & Data);

type FileInfo = { Min_file_id: number, Max_file_id: number };

type ProgressCallback = (arg: { done: number, total: number }) => void;

type RFS_File<R extends ("Energy_File" | "Energy_Per_Day_File")> = FSEntry<RType<R, typeof TYPE_PLA>["Data"][number]> & { Self_index: number };
type RFS_Entry<R extends ("Energy_File" | "Energy_Per_Day_File")> = RFS_File<R> & { Status: "Success" };

type RFS_Events<R extends ("Energy_File" | "Energy_Per_Day_File")> = {
  "sector_loaded": RFS_Entry<R>[],
};
export type EventHandler<R extends ("Energy_File" | "Energy_Per_Day_File"), K extends keyof RFS_Events<R>>
= (ev: Event & { type: K, detail: RFS_Events<R>[K] }) => void;

class RemoteFileSystem<R extends ("Energy_File" | "Energy_Per_Day_File")> extends EventTarget {
  min_file: number;
  max_file: number;
  files: { [k: number]: RFS_File<R> | undefined } = {};

  request: R;
  connection: DeviceConnection<typeof TYPE_PLA>;

  constructor(info: FileInfo, request: R, connection: DeviceConnection<typeof TYPE_PLA>) {
    super();

    this.min_file = info.Min_file_id;
    this.max_file = info.Max_file_id;

    this.request = request;
    this.connection = connection;
  }

  // EventTarget {{{
  dispatchEvent<K extends keyof RFS_Events<R>>(event: Event & { type: K, detail: RFS_Events<R>[K] }) {
    return super.dispatchEvent(event);
  }

  dispatch<K extends keyof RFS_Events<R>>(type: K, detail: RFS_Events<R>[K]) {
    // @ts-ignore
    return this.dispatchEvent(Object.assign(new Event(type), { detail: detail }));
  }

  // @ts-ignore
  addEventListener<K extends keyof RFS_Events<R>>(
    type: K,
    callback: EventHandler<R, K> | null,
    options?: AddEventListenerOptions
  ) {
    // @ts-ignore
    super.addEventListener(type, callback, options);
  }

  // @ts-ignore
  removeEventListener<K extends keyof RFS_Events<R>>(
    type: K,
    callback: EventHandler<R, K> | null,
    options?: AddEventListenerOptions
  ) {
    // @ts-ignore
    super.removeEventListener(type, callback, options);
  }
  // EventTarget }}}

  private async LoadSector(start: number, end: number, abort: AbortSignal) {
    console.assert(start <= end, "start <= end");
    console.assert(end-start+1 <= SECTOR_SIZE, "count <= SECTOR_SIZE");

    if (abort.aborted) return false;

    const response = await this.connection.Fetch(this.request as ("Energy_File" | "Energy_Per_Day_File"), {
      File_ID: start,
      File_Count: end-start+1,
      Tariff_Count: 1,
    });
    if (response.err) {
      if (response.kind !== "canceled") {
        console.warn(`Failed to load sector ${start} -- ${end}`, response.kind);
        // throw new Error("Energy loading failed"); // TODO: error handling
        return false;
      }
      else return false;
    }

    let successful: RFS_Entry<R>[] = [];

    for (let k of KeysOf(response.val.Data)) {
      k = Number(k);
      const file = response.val.Data[k] as typeof this.files[number];
      if (!file) continue;
      file.Self_index = k;
      this.files[k] = file;

      if (file.Status === "Success") successful.push(file);
    }

    this.dispatch("sector_loaded", successful);

    return !abort.aborted;
  }

  /**
   * @param start is included
   */
  public async * IterBack(start: number | null, abort: AbortSignal) {
    if (null === start || start > this.max_file) start = this.max_file;

    for (let i = start; i >= this.min_file; i--) {
      let file = this.files[i];
      if (!file) {
        let load_start = i - SECTOR_SIZE + 1;
        if (load_start < this.min_file) load_start = this.min_file;
        while (this.files[load_start] && load_start <= i) load_start++;
        if (load_start > i) return; // tohle by nemelo nastat, ale kontrola nicemu neuskodi

        if (!await this.LoadSector(load_start, i, abort)) return;

        file = this.files[i]!;
      }

      if (file.Status === "Error") continue;

      yield file;
    }
  }

  private GetUnloadedLoadableSubrangeNearMiddle(start: number, end: number) {
    while (start <= end && this.files[start]) start++;
    while (end >= start && this.files[end])   end++;

    if (end < start) return null;

    if (end - start + 1 < SECTOR_SIZE) {
      return { start, end };
    }

    let mid = (start + end) / 2;

    let max_score = 0;
    let max_score_start = start;

    let nr_unloaded = 0;
    for (let i = start; i < start + SECTOR_SIZE; i++) {
      if (!this.files[i]) nr_unloaded++;
    }

    for (let s = start; s + SECTOR_SIZE <= end; s++) {
      let score = nr_unloaded;
      if (nr_unloaded && s <= mid && mid <= s+SECTOR_SIZE-1) {
        let space_left  = s-start;
        let space_right = end-(s+SECTOR_SIZE);
        let space_avg   = (space_left + space_right) / 2;
        score += (space_left*space_right)/(space_avg*space_avg) * SECTOR_SIZE/4;
      }

      if (score > max_score) {
        max_score = score;
        max_score_start = s;
      }

      if (!this.files[s]) nr_unloaded--;
      if (!this.files[s+SECTOR_SIZE]) nr_unloaded++;
    }

    if (!max_score) return null;

    start = max_score_start;
    end   = max_score_start + SECTOR_SIZE - 1;

    while (start <= end && this.files[start]) start++;
    while (end >= start && this.files[end])   end--;

    console.assert(end >= start, "end >= start");
    if (end < start) return null;

    return { start, end };
  }

  async FindDate(needle: Date, search_space: { start: number, end: number }, abort: AbortSignal) {
    if (search_space.start < this.min_file) search_space.start = this.min_file;
    if (search_space.end   > this.max_file) search_space.end   = this.max_file;
    if (search_space.end < search_space.start) return null;


    let down = search_space.start;
    let up   = search_space.end;

    while (up - down > 1) {
      let last_smaller: RFS_Entry<R> | null = null;
      let first_larger: RFS_Entry<R> | null = null;
      for (let i = down; i <= up; i++) {
        const file = this.files[i];
        if (!file) continue /*for*/;
        if (file.Status === "Error") continue /*for*/;

        if (file.Date_time <= needle) {
          last_smaller = file;
          down = i;
        }

        if (file.Date_time >= needle) {
          first_larger = file;
          up = i;
          break /*for*/;
        }
      }

      // v tomhle bode se prosly vsechny nactene soubory mezi ve starem intervalu [`down` , `up`]
      // pokud je v intervalu (`down`, `up`) mezera, mezisoubory jsou bud `Error` nebo nenactene
      // po prechodu k podintrvalu se tato skutecnost nezmeni

      if (last_smaller) {
        let upper_bound = (down + (needle.valueOf() - last_smaller.Date_time.valueOf()) / 900e3 + 1) >> 0;
        if (up   > upper_bound) up   = upper_bound;
      }

      if (first_larger) {
        let lower_bound = (up   - (first_larger.Date_time.valueOf() - needle.valueOf()) / 900e3 - 1) >> 0;
        if (down < lower_bound) down = lower_bound;
      }

      let to_load = this.GetUnloadedLoadableSubrangeNearMiddle(down, up);
      if (!to_load) break /*while*/; // vsechny soubory v podintervalu jsou nactene (tj. `Error` nebo je podinterval prazdny)

      if (!await this.LoadSector(to_load.start, to_load.end, abort)) {
        return null; // TODO: error handling
      }

      for (let i = to_load.start; i <= to_load.end; i++) {
        const file = this.files[i];
        if (!file) continue /*for*/;
        if (file.Status === "Error") continue /*for*/;

        if (file.Date_time <= needle) {
          last_smaller = file;
          down = i;
        }

        if (file.Date_time >= needle) {
          first_larger = file;
          up = i;
          break /*for*/;
        }
      }
    }

    return { down, up };
  }
}

async function First<T>(i : AsyncIterator<T>) {
  let next = await i.next();
  if (next.done) return null;
  else return next.value;
}

function IndexOfDate(date: Date, res: Resolution) {
  let copy = new Date(date.valueOf());
  // konec je tesne za danym datem
  copy.setTime(copy.getTime() - 60e3);

  switch (res) {
  case Resolution.qh: {
    let minute = copy.getMinutes();
    copy.setMinutes(minute - (minute%15), 0, 0);
  } break;
  case Resolution.hour:
    copy.setMinutes(0, 0, 0);
    break;
  case Resolution.day:
    copy.setHours(0, 0, 0, 0);
    break;
  case Resolution.month:
    copy.setDate(1);
    copy.setHours(0, 0, 0, 0);
    break;
  }

  return (copy.valueOf() / 60e3) >> 0;
}

function BinSearchGE<A, B>(el: A, arr: B[], a_ge_b: (a: A, b: B) => boolean) {
  if (!arr.length) return 0;
  let low = 0, high = arr.length;

  while (low < high && low < arr.length) {
    let mid = (low + high) >> 1;
    if (a_ge_b(el, arr[mid]))
      low  = mid + 1;
    else
      high = mid;
  }

  return high;
}

class IndexDateMap {
  public MaxPossibleIndexRange(start: Date, end: Date) : { start: number, end: number } {
    let range = this.InternalIndexRange(start, end);
    return {
      start: range.down < 0 ? -Infinity : this.arr[range.down].index,
      end: range.up >= this.arr.length ? +Infinity : this.arr[range.up].index,
    };
  }

  public InsertKnown(points: { index: number, time: Date }[]) {
    if (!points.length) return;
    points.sort((a, b) => a.index - b.index);

    let range = this.InternalIndexRange(points[0].time, points[points.length-1].time);

    let merged: typeof this.arr = [];
    if (range.down >= 0) merged = merged.concat(this.arr.slice(0, range.down));

    merged = merged.concat(IndexDateMap.Merge(this.arr.slice(range.down, range.up), points, (a, b) => a.index >= b.index));

    merged = merged.concat(this.arr.slice(range.up));

    this.arr = merged;
  }

  private arr: { index: number, time: Date }[] = [];
  private InternalIndexRange(start: Date, end: Date) {
    let down = BinSearchGE(start, this.arr, (a, b) => a >= b.time);
    if (down >= this.arr.length || start < this.arr[down].time) down--;

    let up = BinSearchGE(end, this.arr, (a, b) => a >= b.time);

    return { down, up };
  }

  private static Merge<T>(a: T[], b: T[], a_ge_b: (a:T, b:T) => boolean) : T[] {
    let mix: T[] = new Array(a.length + b.length);
    let mix_len = 0;

    let ia = 0, ib = 0;

    while (ia < a.length && ib < b.length) {
      if (a_ge_b(a[ia], b[ib])) {
        if (a_ge_b(b[ib], a[ia])) ia++; // pokud se rovnaji, deduplikujeme
        mix[mix_len++] = b[ib++];
      }
      else {
        mix[mix_len++] = a[ia++];
      }
    }

    while (ia < a.length) mix[mix_len++] = a[ia++];
    while (ib < b.length) mix[mix_len++] = b[ib++];

    return mix.slice(0, mix_len);
  }
}

function PrevMidnight(date: Date) {
  let copy = new Date(date.valueOf());
  copy.setHours(0,0,0,0);
  if (copy.valueOf() === date.valueOf()) copy.setDate(copy.getDate()-1);

  console.assert(copy < date, "copy < date");
  return copy;
}

class Cache {
  day_fs: RemoteFileSystem<"Energy_Per_Day_File">;
  det_fs: RemoteFileSystem<"Energy_File">;
  det_time_map = new IndexDateMap();

  public live_value: null | { time: Date, val: tuple<bigint, 6> } = null;

  on_progress: ProgressCallback;

  public SetConnection(val: DeviceConnection<typeof TYPE_PLA>) {
    this.det_fs.connection = val;
    this.day_fs.connection = val;
  }

  constructor(day_info: FileInfo, det_info: FileInfo, connection: DeviceConnection<typeof TYPE_PLA>, on_progress: ProgressCallback) {
    this.day_fs = new RemoteFileSystem(day_info, "Energy_Per_Day_File", connection);
    this.det_fs = new RemoteFileSystem(det_info, "Energy_File", connection);
    this.on_progress = on_progress;

    this.day_fs.addEventListener("sector_loaded", (ev) => {
      const points =
        ev.detail
        .filter(f => f.ID_detail !== undefined)
        .map(f => ({ index: f.ID_detail!, time: f.Date_time }));
      this.det_time_map.InsertKnown(points);
    });
  }

  private async * IterDaysBack(start: Date, abort: AbortSignal) {
    let last_det = await First(this.det_fs.IterBack(null, abort));
    let last_day = await First(this.day_fs.IterBack(null, abort));

    let next = null;

    if (last_det && last_det.Date_time <= start && (!last_day || last_det.Date_time > last_day.Date_time)) {
      next = { ...last_det, ID_detail: last_det.Self_index };
      yield last_det;
    }

    for await (let day_file of this.day_fs.IterBack(null, abort)) {
      if (day_file.Date_time > start || !next) {
        next = day_file;
        continue;
      }

      let counter = 0;
      for (
        let prev_midnight = PrevMidnight(next.Date_time);
        day_file.Date_time < prev_midnight;
        prev_midnight = PrevMidnight(next.Date_time)
      ) {
        let det_index : number | undefined = (await this.det_fs.FindDate(prev_midnight, { start: day_file.ID_detail ?? -Infinity, end: next.ID_detail ?? +Infinity }, abort))?.down;
        if (det_index === undefined) break;

        let det_file : RFS_Entry<"Energy_File"> | null = await First(this.det_fs.IterBack(det_index, abort));
        if (!det_file) break;

        next = { ...det_file, ID_detail: det_file.Self_index };
        console.assert(next.Date_time <= prev_midnight, "next.Date_time <= prev_midnight", next.Date_time, prev_midnight);
        yield det_file;

        counter++;
        if (counter > 32) return;
      }

      next = day_file;
      yield day_file;

      if (day_file.Cleared && day_file.ID_detail) {
        let before_clear = await First(this.det_fs.IterBack(day_file.ID_detail-1, abort));
        if (before_clear) {
          next = { ...before_clear, ID_detail: before_clear.Self_index };
          yield before_clear;
        }
      }
    }
  }

  public async * LoadInterval(start: Date, end: Date, res: Resolution, abort: AbortSignal) {
    if (end < start) return;
    start = new Date(start.valueOf());
    end   = new Date(end.valueOf());

    let parts: { [k: number]: tuple<number, 6> } = {};


    // ----- nacteni hodnot -----
    let iterator = null;
    switch (res) {
    case Resolution.qh:
    case Resolution.hour: {
      let irange = this.det_time_map.MaxPossibleIndexRange(start, end);

      let end_index = (await this.det_fs.FindDate(end, irange, abort))?.down;
      if (undefined !== end_index) {
        iterator = this.det_fs.IterBack(end_index, abort);
      }
    } break;

    case Resolution.day:
    case Resolution.month: {
      start.setHours(0, 0, 0, 0);
      end.setDate(end.getDate()+2);

      iterator = this.IterDaysBack(end, abort);
    } break;
    }

    let last_file = null as null | RFS_Entry<"Energy_File">;

    if (iterator) {
      let last_progress_report = new Date();
      let prev = null;
      for await (let file of iterator) {
        if (!last_file || last_file.Date_time < file.Date_time) last_file = file;

        if (prev && !prev.Cleared) {
          let index = IndexOfDate(prev.Date_time, res);
          let part = parts[index];
          if (!part) part = parts[index] = [0,0,0,0,0,0];

          parts[index] = part = vinc(part, vsub_div_10(prev.T1, file.T1));
        }

        if (file.Date_time < start) break;
        prev = file;

        // report approximate progress
        let now = new Date();
        if (now.valueOf() - last_progress_report.valueOf() > 250) {
          last_progress_report = now;
          yield { parts, last_file, curr_file: file };
        }
      }
    }

    yield { parts, last_file };
  }

  public async * ReadInterval(start: Date, end: Date, res: Resolution, abort: AbortSignal) {
    // ----- nastaveni rozsahu casu -----
    start = new Date(start.valueOf());
    end = new Date(end.valueOf());

    let step_time : (date_ref: Date) => void;

    switch (res) {
    case Resolution.qh: {
      let start_min = start.getMinutes();
      start.setMinutes(start_min - start_min%15 + 15, 0, 0);
      let end_min = end.getMinutes();
      end.setMinutes(end_min - end_min%15, 0, 0);

      step_time = (val) => val.setMinutes(val.getMinutes() + 15);
    } break;

    case Resolution.hour:
      start.setMinutes(60, 0, 0);
      end.setMinutes(0, 0, 0);

      step_time = (val) => val.setHours(val.getHours() + 1);
      break;

    case Resolution.day:
      start.setHours(12, 0, 0, 0);
      end.setHours(12, 0, 0, 0);

      step_time = (val) => val.setDate(val.getDate() + 1);
      break;

    case Resolution.month:
      start.setHours(12, 0, 0, 0);
      start.setDate(1);
      end.setHours(12, 0, 0, 0);
      end.setMonth(end.getMonth()+1, 0);

      step_time = (val) => val.setMonth(val.getMonth() + 1);
      break;

    default: {
      let _ : never = res;
      console.error("Unhandled resolution", _);
    } return;
    }

    let data : EnergyData = [];
    let result = {
      progress: 0,
      result: data,
    };
    yield result;

    // ----- prirazeni hodnot casum -----
    for await (let { parts, last_file, curr_file: file } of this.LoadInterval(start, end, res, abort)) {
      if (last_file && this.live_value) {
        parts = { ...parts };

        let live_index = IndexOfDate(this.live_value.time, res);
        parts[live_index] ??= zeros;

        parts[live_index] = vinc([...parts[live_index]] as typeof parts[number], vsub_div_10(this.live_value.val, last_file.T1));
      }

      let i = 0;
      for (let time = new Date(start.valueOf()); time <= end; step_time(time)) {
        let values = parts[IndexOfDate(time, res)] ?? zeros;

        data[i++] = {
          time: new Date(time.valueOf()),
          values: values,
        };
      }

      if (!file) {
        result.progress = 1;
      }
      else {
        let reqd = end.valueOf() - start.valueOf();
        let done = end.valueOf() - file.Date_time.valueOf();
        result.progress = (1+done)/(1+reqd);
      }

      yield result;
    }

    result.progress = 1;
    yield result;
  }
}



const zeros = [0, 0, 0, 0, 0, 0] as tuple<number, 6>;
const emptyFolderInfo = { Data: { Min_file_id: 1, Max_file_id: 0 } };

function ParseOnlineElmer(elmer: energy) : tuple<bigint, 6> {
  return [
    BigInt.asUintN(64, BigInt(elmer.Consumption.Active_kWh)),
    BigInt.asUintN(64, BigInt(elmer.Consumption.Inductive_kvarh)),
    BigInt.asUintN(64, BigInt(elmer.Consumption.Capacity_kvarh)),
    BigInt.asUintN(64, BigInt(elmer.Distribution.Active_kWh)),
    BigInt.asUintN(64, BigInt(elmer.Distribution.Inductive_kvarh)),
    BigInt.asUintN(64, BigInt(elmer.Distribution.Capacity_kvarh)),
  ];
}

let cache : Cache | null = null;
export function useEnergyLoader(
  start : Date,
  end : Date,
  resolution : Resolution,
) {
  const mounted = useMounted();

  const rangeLoader = useLoader("Energy_Per_Day_Folder_Info", emptyFolderInfo, { disableWhenLoaded: true });
  const detailRangeLoader = useLoader("Energy_Folder_Info",   emptyFolderInfo, { disableWhenLoaded: true });
  const onlineLoader = useLoader("Online", null);
  const data = useRef<EnergyData>([]);
  const [minorDataVersion, advanceMinDV] = useReducer((v : number) => v + 1, 0);
  const [majorDataVersion, advanceMajDV] = useReducer((v : number) => v + 1, 0);
  const [progress, setProgress] = useState(0);
  const [rangesLoaded, setRangesLoaded] = useState(false);

  const lastLiveIndex = useRef(-1);

  const connection = useDeviceConnection();
  useEffect(function UpdateCacheConnection() {
    if (!connection.IsFrom(TYPE_PLA)) return; // TODO: drop cache?

    cache?.SetConnection(connection);

    if (config.DEBUG) console.warn("connection changing");
  }, [connection]);

  useAsyncEffect(async function LoadToday(abort) {
    if (!cache) return;

    if (onlineLoader.data && majorDataVersion > 1 && IsFromDevice(onlineLoader.data, TYPE_PLA) && connection.IsFrom(TYPE_PLA)) {
      let last = undefined;

      cache.live_value = {
        time: new Date(onlineLoader.data.Head.Date_time),
        val:  ParseOnlineElmer(onlineLoader.data.Data.Energy_T1),
      };

      // IndexOfDate je o minutu opozden, takze by melo byt dost casu na vytvoreni noveho zaznamu
      const liveIndex = IndexOfDate(cache.live_value.time, Resolution.qh);
      if (liveIndex !== lastLiveIndex.current) {
        lastLiveIndex.current = liveIndex;
        detailRangeLoader.forceReload();
        return;
      }

      for await (last of cache.ReadInterval(start, end, resolution, abort)) {
        if (mounted.current) setProgress(last.progress);
      }
      if (last) {
        data.current = (last.result);
        advanceMinDV();
      }
    }
  }, [onlineLoader.data, majorDataVersion]);

  useEffect(function SpecifyFileRanges() {
   setRangesLoaded(false);

    if (!connection.IsFrom(TYPE_PLA)) return;

    if (rangeLoader.data === emptyFolderInfo) return;
    if (detailRangeLoader.data === emptyFolderInfo) return;

    cache ??= new Cache(rangeLoader.data.Data, detailRangeLoader.data.Data, connection, (prog) => {
      if (!mounted.current) return;
      setProgress((prog.done+2) / (prog.total+2));
    });
    cache.det_fs.max_file = detailRangeLoader.data.Data.Max_file_id;
    setRangesLoaded(true);
  }, [rangeLoader.data.Data, detailRangeLoader.data.Data]);

  useAsyncEffect(async function LoadDesiredRange(abort) {
    if (!connection.IsFrom(TYPE_PLA) || !cache) return;

    for await (let newData of cache.ReadInterval(start, end, resolution, abort)) {
      if (mounted.current) setProgress(newData.progress);
      if (newData.progress === 1) {
        data.current = newData.result;
        advanceMinDV();
      }
    }

    advanceMajDV();
  }, [start, end, resolution, rangesLoaded]);

  return {
    loaders: [rangeLoader, detailRangeLoader, onlineLoader] as ReturnType<typeof useLoader>[],
    data,
    minorDataVersion,
    suggestedRes: resolution,  // TODO:
    progress,
    majorDataVersion,
    onlnieData: onlineLoader.data
  }
}

export function ResetEnergyCache() {
  cache = null;
}

